<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <v-icon left color="primary">security</v-icon>
        Phân quyền chức năng theo vai trò
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="savePermissions"
          :loading="saving"
          :disabled="saving || loading"
        >
          Lưu phân quyền
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="loading" type="info" dense outlined class="mb-4">
          Đang tải dữ liệu phân quyền...
        </v-alert>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Chức năng</th>
              <th v-for="role in roles" :key="role.value">{{ role.name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="feature in features" :key="feature.key">
              <td>{{ feature.label }}</td>
              <td v-for="role in roles" :key="role.value">
                <div class="permission-actions">
                  <v-tooltip
                    bottom
                    v-for="action in permissionActions"
                    :key="action.value"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        small
                        :aria-label="action.tooltip"
                        class="permission-action-btn"
                        :color="isActive(feature.key, role.value, action.value) ? action.color : ''"
                        :outlined="!isActive(feature.key, role.value, action.value)"
                        @click="setPermission(feature.key, role.value, action.value)"
                      >
                        <v-icon small>{{ action.icon }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ action.tooltip }}</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import configs from '@/configs';
import { sendErrorNotice, sendSuccessNotice } from '@/commons/utils';
import { PERMISSION_FEATURES, buildPermissionTemplate } from '@/configs/permissionFeatures';

const permissionActions = [
  { value: 2, icon: 'mdi-lock-open-variant', color: 'success', tooltip: 'Toàn quyền: xem & chỉnh sửa' },
  { value: 1, icon: 'mdi-eye', color: 'warning', tooltip: 'Chỉ xem: xem được màn hình' },
  { value: 0, icon: 'mdi-lock', color: 'error', tooltip: 'Không có quyền truy cập' }
];

export default {
  name: 'PermissionMatrix',
  data() {
    return {
      roles: _.sortBy(_.filter(configs.ROLES, (obj) => { return obj.value != configs.ROLE_TYPE.Customer; }), 'name'),
      features: PERMISSION_FEATURES,
      permissionActions,
      matrixDraft: buildPermissionTemplate(_.sortBy(_.filter(configs.ROLES, (obj) => { return obj.value != configs.ROLE_TYPE.Customer; }), 'name')),
      saving: false,
      loading: false
    };
  },
  computed: {
    permissionMatrix() {
      const moduleState = this.$store.state.permission;
      return moduleState && moduleState.matrix ? moduleState.matrix : buildPermissionTemplate(this.roles);
    }
  },
  watch: {
    permissionMatrix: {
      immediate: true,
      handler(val) {
        const snapshot = JSON.parse(JSON.stringify(val || buildPermissionTemplate(this.roles)));
        this.matrixDraft = snapshot;
      }
    }
  },
  created() {
    this.loadPermissions();
  },
  methods: {
    async loadPermissions() {
      this.loading = true;
      try {
        await this.$store.dispatch('permission/loadMatrix', { force: true });
      } catch (error) {
        console.error('loadPermissions error', error);
        sendErrorNotice(this.extractErrorMessage(error, 'Không thể tải phân quyền.'));
      } finally {
        this.loading = false;
      }
    },
    async savePermissions() {
      if (this.loading) {
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('permission/saveMatrix', this.matrixDraft);
        sendSuccessNotice('Đã lưu phân quyền!');
      } catch (error) {
        console.error('savePermissions error', error);
        sendErrorNotice(this.extractErrorMessage(error, 'Lưu phân quyền thất bại.'));
      } finally {
        this.saving = false;
      }
    },
    setPermission(featureKey, roleValue, permissionValue) {
      this.$set(this.matrixDraft[featureKey], roleValue, permissionValue);
    },
    isActive(featureKey, roleValue, permissionValue) {
      return this.matrixDraft[featureKey][roleValue] === permissionValue;
    },
    extractErrorMessage(error, fallback) {
      if (!error) {
        return fallback;
      }
      if (error.body && error.body.reason) {
        return error.body.reason;
      }
      if (error.data && error.data.reason) {
        return error.data.reason;
      }
      if (error.response && error.response.data && error.response.data.reason) {
        return error.response.data.reason;
      }
      return error.message || fallback;
    }
  }
};
</script>

<style scoped>
.permission-actions {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.permission-action-btn {
  min-width: 36px;
  padding: 0 4px;
}
</style>
